/**
 * @file 手書きのキャンバスである (メモの手書きのモーダルと、ページの上の書き込みの共通部品)。
 *
 * @what: 文書 (線の集まり) を表示し、Pointer Events で新しい線を受け取って親に渡す。確定した線は
 *   下絵のキャンバスに塗っておき、書いている間はその上に今の線だけを重ねる。表示サイズ × 画素密度で
 *   実サイズを決め、論理座標との倍率で塗る。線を1本足したときは下絵を作り直さず、その線だけを
 *   塗り足す。※なぜなら作り直すと、書くたびにそのページの線を全部塗り直すことになり、線が増える
 *   ほど1本ごとの待ちが伸びるためである。
 * @why: メモの手書きとページの書き込みで、書く操作は同じである。違うのは下地 (白い紙か透明か) と
 *   大きさだけなので、そこを props にして1つにする。
 * @failure: Canvas が使えない環境 (テスト) では表示だけ止め、線の受け渡しは動作する。
 *
 * つついて書けるように、書ける間は触れたときの既定の動作を止める。※なぜなら iPad のブラウザは
 * `touch-action: none` を当てても2回続けて触れたものを拡大のダブルタップと見て、2回目を web の側へ
 * 渡さないためである (代表の端末で再現、2026-09-11)。文字を1つ書くたびにペンを離す書き方では、
 * 2回に1回書けない形で出る。
 *
 * 手のひらの扱いは、ペンの入力が来た時点で指の線を捨ててペンに切り替える形にしている。
 * ※なぜなら手のひらが先に紙面へ触れると、その指が線を始めてしまい、続くペンの入力が
 * 「すでに書いている線がある」として捨てられるためである。一度ペンを使ったあとは指の入力を
 * 受けない。この印は penSeenRef を渡せば呼び出し側が持てる (ページを送っても戻らない)。
 */

import { useCallback, useEffect, useRef, type CSSProperties, type MutableRefObject } from "react";
import {
  PEN_COLORS,
  PEN_SIZES,
  paintHandwriting,
  paintStroke,
  type HandwritingDoc,
  type HandwritingPoint,
  type HandwritingStroke,
  type HandwritingTool,
} from "../model/handwriting";

export function InkCanvas({
  doc,
  onStroke,
  tool,
  colorId,
  sizeId,
  enabled = true,
  transparent = false,
  penSeenRef,
  className,
  style,
  ariaLabel = "手書きのキャンバス",
}: {
  doc: HandwritingDoc;
  /** 線を1本書き終えたときに呼ぶ。 */
  onStroke: (stroke: HandwritingStroke) => void;
  tool: HandwritingTool;
  colorId: string;
  sizeId: string;
  /** 偽なら表示だけで、入力を受けない (ページの上で読んでいるとき)。 */
  enabled?: boolean;
  /** 真なら下地を塗らず、消しゴムは本当に消す (ページの上の書き込み)。 */
  transparent?: boolean;
  /** ペンを使ったかの印を呼び出し側が持つとき (省略すると、この部品の中だけで持つ)。 */
  penSeenRef?: MutableRefObject<boolean>;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // 確定した線だけを塗った下絵である。書いている間はこれの上に今の線だけを重ねる。
  const cacheRef = useRef<HTMLCanvasElement | null>(null);
  // 下絵に塗り終えた線の本数と、その最後の1本である (塗り足しでよいかの判定に使う)。
  const paintedRef = useRef<{ count: number; last: HandwritingStroke | null }>({
    count: 0,
    last: null,
  });
  const activeRef = useRef<{ pointerId: number; stroke: HandwritingStroke } | null>(null);
  // Pencil を一度でも使ったら、以後の指の入力 (手のひら) は無視する。
  const ownPenSeenRef = useRef(false);
  const penSeen = penSeenRef ?? ownPenSeenRef;
  const docRef = useRef(doc);
  docRef.current = doc;

  const scaleOf = useCallback((canvas: HTMLCanvasElement) => canvas.width / docRef.current.width, []);

  const rebuildCache = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cache = cacheRef.current;
    if (!cache) {
      cache = document.createElement("canvas");
      cacheRef.current = cache;
    }
    cache.width = canvas.width;
    cache.height = canvas.height;
    const d = docRef.current;
    paintedRef.current = { count: d.strokes.length, last: d.strokes[d.strokes.length - 1] ?? null };
    const ctx = cache.getContext("2d");
    if (!ctx) return;
    paintHandwriting(ctx, d, scaleOf(canvas), transparent);
  }, [scaleOf, transparent]);

  /** 下絵を文書に合わせる。書き足しただけなら、足りない線を塗り足す。 */
  const syncCache = useCallback(() => {
    const canvas = canvasRef.current;
    const cache = cacheRef.current;
    if (!canvas) return;
    if (!cache || cache.width !== canvas.width || cache.height !== canvas.height) {
      rebuildCache();
      return;
    }
    const strokes = docRef.current.strokes;
    const painted = paintedRef.current;
    // 減っている (取り消し・全消し) か、前の線が入れ替わっているときは作り直す。
    if (painted.count > strokes.length) {
      rebuildCache();
      return;
    }
    if (painted.count > 0 && strokes[painted.count - 1] !== painted.last) {
      rebuildCache();
      return;
    }
    if (painted.count === strokes.length) return;
    const ctx = cache.getContext("2d");
    if (!ctx) return;
    for (let i = painted.count; i < strokes.length; i++) {
      paintStroke(ctx, strokes[i]!, scaleOf(canvas), transparent);
    }
    paintedRef.current = { count: strokes.length, last: strokes[strokes.length - 1] ?? null };
  }, [rebuildCache, scaleOf, transparent]);

  const paintAll = useCallback(() => {
    const canvas = canvasRef.current;
    const cache = cacheRef.current;
    if (!canvas || !cache) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(cache, 0, 0);
    const active = activeRef.current;
    if (active) paintStroke(ctx, active.stroke, scaleOf(canvas), transparent);
  }, [scaleOf, transparent]);

  // 実サイズを表示サイズ × 画素密度に合わせる (拡大しても線がぼやけない)。大きさが変わって
  // いないときは canvas を作り直さない。※なぜなら width への代入は同じ値でも中身を捨てるため、
  // 線を1本足すたびに全部を塗り直すことになるためである。
  const layout = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const d = docRef.current;
    const cssW = rect.width || d.width;
    const dpr = typeof window.devicePixelRatio === "number" ? window.devicePixelRatio : 1;
    const width = Math.max(1, Math.round(cssW * dpr));
    const height = Math.max(1, Math.round(((cssW * d.height) / d.width) * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      rebuildCache();
    } else {
      syncCache();
    }
    paintAll();
  }, [rebuildCache, syncCache, paintAll]);

  useEffect(() => {
    layout();
    if (typeof ResizeObserver === "undefined") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => layout());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [layout]);

  useEffect(() => {
    layout();
  }, [doc, layout]);

  // つついて書くと、2回目のつつきがブラウザの拡大のダブルタップに取られて届かない。
  // ※なぜなら iPad の Safari と Chrome は `touch-action: none` を当てても、なお2回続けて
  //   触れたものを1組として見るためである (代表の端末で再現、2026-09-11)。既定の動作を止めれば
  //   届くようになる。React の購読は passive で止められないので、要素に直に張る。
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !enabled) return;
    const stop = (e: TouchEvent) => e.preventDefault();
    canvas.addEventListener("touchstart", stop, { passive: false });
    return () => canvas.removeEventListener("touchstart", stop);
  }, [enabled]);

  const pointOf = (e: React.PointerEvent<HTMLCanvasElement>): HandwritingPoint => {
    const rect = e.currentTarget.getBoundingClientRect();
    const d = docRef.current;
    const cssW = rect.width || d.width;
    const k = d.width / cssW;
    const pressure = e.pointerType === "pen" ? Math.max(0.05, e.pressure || 0.5) : 0.5;
    return [(e.clientX - rect.left) * k, (e.clientY - rect.top) * k, pressure];
  };

  /**
   * 書きかけの線を終える。keep が真なら線として残し、偽なら捨てる。
   * 手のひらの線をペンに明け渡すときは捨て、離れた合図が届かなかった線は残す。
   */
  const endActive = (canvas: HTMLCanvasElement, keep: boolean) => {
    const active = activeRef.current;
    if (!active) return;
    activeRef.current = null;
    try {
      canvas.releasePointerCapture(active.pointerId);
    } catch {
      // 捕まえていない環境 (テスト) では何もしない
    }
    if (keep && active.stroke.points.length > 0) onStroke(active.stroke);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!enabled) return;
    if (e.pointerType === "pen") penSeen.current = true;
    if (e.pointerType === "touch" && penSeen.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const active = activeRef.current;
    if (active) {
      // 指やマウスは、書いている線を横取りしない。
      if (e.pointerType !== "pen") return;
      // 前もペンなら、離れた合図が届かなかった線である (紙に触れるペンは1本だけである)。
      // その線は残し、指で書きかけの線は手のひらとみなして捨てる。
      endActive(e.currentTarget, active.stroke.pen);
    }
    const color = PEN_COLORS.find((c) => c.id === colorId)?.color ?? PEN_COLORS[0]!.color;
    const size = PEN_SIZES.find((s) => s.id === sizeId)?.size ?? PEN_SIZES[1]!.size;
    activeRef.current = {
      pointerId: e.pointerId,
      stroke: { tool, color, size, pen: e.pointerType === "pen", points: [pointOf(e)] },
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // 対応していない環境 (テスト) では捕まえずに続ける
    }
    paintAll();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== e.pointerId) return;
    active.stroke.points.push(pointOf(e));
    paintAll();
  };

  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== e.pointerId) return;
    activeRef.current = null;
    if (active.stroke.points.length > 0) onStroke(active.stroke);
    else paintAll();
  };

  /** 紙面の外へ出たときである。捕まえている間は離すまで線を続ける (端で切らない)。 */
  const leaveStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) return;
    } catch {
      // 対応していない環境 (テスト) では、そのまま線を終える
    }
    finishStroke(e);
  };

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={style}
      role="img"
      aria-label={ariaLabel}
      data-strokes={doc.strokes.length}
      data-enabled={enabled ? "true" : "false"}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishStroke}
      onPointerCancel={finishStroke}
      onPointerLeave={leaveStroke}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
